import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useToast } from "../components/Toast";
import type { ListCategorySettings } from "../features/quotes/CategoryPicker";
import { QUOTE_CATEGORIES_CHANGED, QUOTE_VIEWS, saveView, viewOf } from "../features/quotes/quoteViews";
import type { QuoteView } from "../features/quotes/quoteViews";

// 左側「報價單」底下的二級選單(業主 2026-10-15):全部 / 已結案 / 未結案。
// 只在報價單那幾頁展開。亮哪一個看目前存著的分類(見 `quoteViews.ts`)。

export function QuoteSubNav({ onPicked }: { onPicked?: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [current, setCurrent] = useState<QuoteView | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get<ListCategorySettings>("/users/me/list-categories")
      .then((got) => alive && setCurrent(viewOf(got?.quote_list_categories ?? [])))
      .catch(() => undefined);
    const onChanged = (event: Event) => setCurrent(viewOf((event as CustomEvent<string[]>).detail ?? []));
    window.addEventListener(QUOTE_CATEGORIES_CHANGED, onChanged);
    return () => {
      alive = false;
      window.removeEventListener(QUOTE_CATEGORIES_CHANGED, onChanged);
    };
  }, []);

  const pick = async (view: QuoteView) => {
    if (busy) return;
    setBusy(true);
    try {
      await saveView(view);
      navigate("/quotes");
      onPicked?.();
    } catch (error) {
      toast.push(error instanceof Error ? error.message : "儲存失敗", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ul aria-label="報價單分類" className="m-0 mt-0.5 flex list-none flex-col gap-0.5 p-0 pl-7">
      {QUOTE_VIEWS.map((view) => {
        const active = current === view.key;
        return (
          <li key={view.key}>
            <button
              type="button"
              aria-current={active ? "true" : undefined}
              disabled={busy}
              onClick={() => void pick(view.key)}
              className={
                "w-full rounded-[var(--radius)] px-3 py-1 text-left text-sm " +
                (active
                  ? "font-medium text-[var(--accent-hover)]"
                  : "text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]")
              }
            >
              {view.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
